import { Alert, Button, Card, Skeleton, Statistic, Typography } from "antd";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { fetchPublicDashboard, PublicDashboardError, type PublicDashboardData } from "./publicDashboardApi";
import { PublicCemeteryMap } from "./PublicCemeteryMap";
import "./publicPortal.css";
import "./publicHomeStatistics.css";

const statistics = [
  { key: "cemeteryCount", label: "Số lượng nghĩa trang", icon: "M10 52h44M16 52V25h32v27M12 25l20-13 20 13M24 52V34h16v18M26 25h12M32 7v5M8 52v5M56 52v5" },
  { key: "graveCount", label: "Số lượng mộ", icon: "M8 53h48M12 49h40M14 49V19q9-14 18 0v30M32 49V19q9-14 18 0v30M20 27h6M20 33h6M20 39h6M38 27h6M38 33h6M38 39h6" },
  { key: "totalVisits", label: "Lượt viếng thăm", icon: "M32 11a8 8 0 1 0 0 16 8 8 0 0 0 0-16M16 53c0-9 7-15 16-15s16 6 16 15M15 22a5 5 0 1 0 0 10 5 5 0 0 0 0-10M6 53c0-7 4-10 9-10M49 22a5 5 0 1 0 0 10 5 5 0 0 0 0-10M49 43c5 0 9 3 9 10" },
  { key: "dailyIncenseCount", label: "Dâng hương trong ngày", icon: "M17 34h30l-4 15H21zM13 34h38M24 49v5M40 49v5M27 34V18M37 34V15M27 18c-5-3 5-7 0-10M37 15c5-2-5-5 0-8" },
] as const;
// ponytail: số cứng tạm cho demo, vẫn gọi backend; xoá map này để hiện lại số thật.
const DISPLAY_OVERRIDE: Partial<Record<(typeof statistics)[number]["key"], number>> = { cemeteryCount: 127, graveCount: 37689 };
const UNIDENTIFIED_GRAVES_DEMO = 13656;

const SCENE_CTA = "Mở bản đồ";
const GUIDE_TITLE = "Ba bước để đứng trước một phần mộ";
const GUIDE_STEPS: readonly {
  readonly title: string;
  readonly desc: string;
  readonly hint: string;
}[] = [
  {
    title: "Mở sơ đồ số",
    desc: "Mở sơ đồ số để xem toàn cảnh nghĩa trang và Sổ vị trí.",
    hint: "Bao quát toàn cảnh không gian ảo và bản đồ quy hoạch phân khu",
  },
  {
    title: "Định vị phần mộ",
    desc: "Chọn một ô mộ trên Sổ vị trí, hoặc bấm vào phần mộ trong khung ba chiều.",
    hint: "Tương tác trực tiếp trên lưới sơ đồ hoặc không gian ba chiều thời gian thực",
  },
  {
    title: "Tra cứu hồ sơ",
    desc: "Đọc mã vị trí hiện ở khung bên cạnh, rồi mở hồ sơ minh họa của phần mộ đó.",
    hint: "Xác thực danh tính, thông tin quy tập và thông điệp tưởng niệm minh bạch",
  },
];

const SIMULATION_NOTICE =
  "Dữ liệu mô phỏng phục vụ trình diễn và thử nghiệm, không thay thế hồ sơ nghiệp vụ của hệ thống.";

const PILLARS: readonly {
  readonly tag: string;
  readonly title: string;
  readonly desc: string;
}[] = [
  {
    tag: "KHÔNG GIAN 3D WEBGL",
    title: "Mô hình ba chiều trực quan",
    desc: "Tái hiện không gian nghĩa trang chân thực, định vị từng hàng bia và lối đi với góc nhìn tự do.",
  },
  {
    tag: "ĐỊNH VỊ GIS ĐỒNG BỘ",
    title: "Sổ vị trí thông minh",
    desc: "Liên kết đồng bộ giữa sơ đồ phân khu và tọa độ thực địa, tra cứu nhanh chóng và chính xác.",
  },
  {
    tag: "DỮ LIỆU MỞ MINH BẠCH",
    title: "Hồ sơ minh họa minh bạch",
    desc: "Công khai dữ liệu phục vụ thân nhân và nhân dân viếng thăm, tưởng nhớ từ xa một cách trang trọng.",
  },
];

/* ── 4. CÁC BIỂU TƯỢNG SVG CHUYÊN BIỆT ── */

function IconNationalStar() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M12 2L14.9 8.26L21.8 9.27L16.8 14.14L18 21.02L12 17.77L6 21.02L7.2 14.14L2.2 9.27L9.1 8.26L12 2Z"
        fill="url(#starGoldGrad)"
        stroke="#F5D38A"
        strokeWidth="0.8"
      />
      <defs>
        <linearGradient id="starGoldGrad" x1="12" y1="2" x2="12" y2="21.02" gradientUnits="userSpaceOnUse">
          <stop stopColor="#FDE68A" />
          <stop offset="0.6" stopColor="#C8933A" />
          <stop offset="1" stopColor="#92400E" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function IconSpatial3D() {
  return (
    <svg width="34" height="34" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M16 3L28 9.5V22.5L16 29L4 22.5V9.5L16 3Z" stroke="#E2C285" strokeWidth="1.75" strokeLinejoin="round" />
      <path d="M16 3V16M28 9.5L16 16M4 9.5L16 16" stroke="#C8933A" strokeWidth="1.5" strokeLinejoin="round" opacity="0.85" />
      <path d="M16 16V29" stroke="#E2C285" strokeWidth="1.5" />
      <circle cx="16" cy="16" r="3.5" fill="#F2B24C" />
      <circle cx="16" cy="3" r="2" fill="#E2C285" />
      <circle cx="28" cy="9.5" r="2" fill="#E2C285" />
      <circle cx="4" cy="9.5" r="2" fill="#E2C285" />
      <circle cx="28" cy="22.5" r="2" fill="#E2C285" />
      <circle cx="4" cy="22.5" r="2" fill="#E2C285" />
      <circle cx="16" cy="29" r="2" fill="#E2C285" />
    </svg>
  );
}

function IconLocationGrid() {
  return (
    <svg width="34" height="34" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="4" y="4" width="24" height="24" rx="5" stroke="#E2C285" strokeWidth="1.75" />
      <path d="M4 12H28M4 20H28M12 4V28M20 4V28" stroke="#C8933A" strokeWidth="1" strokeDasharray="2 2" opacity="0.45" />
      <circle cx="16" cy="14" r="5" stroke="#F2B24C" strokeWidth="1.5" fill="rgba(242, 178, 76, 0.15)" />
      <circle cx="16" cy="14" r="2.2" fill="#F2B24C" />
      <path d="M16 19V24" stroke="#F2B24C" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconSacredScroll() {
  return (
    <svg width="34" height="34" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M6 7C6 5.34315 7.34315 4 9 4H23C24.6569 4 26 5.34315 26 7V23C26 24.6569 24.6569 26 23 26H9C7.34315 26 6 24.6569 6 23V7Z" stroke="#E2C285" strokeWidth="1.75" />
      <path d="M10 9H22M10 13H22M10 17H16" stroke="#C8933A" strokeWidth="1.5" strokeLinecap="round" opacity="0.85" />
      <circle cx="21" cy="20" r="3.5" fill="#C8933A" />
      <path d="M21 18L21.8 19.6L23.5 19.8L22.2 21L22.6 22.7L21 21.8L19.4 22.7L19.8 21L18.5 19.8L20.2 19.6L21 18Z" fill="#081426" />
    </svg>
  );
}


function IconSpatialCompass() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" />
      <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" />
      <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" />
    </svg>
  );
}

/* ── COMPONENT CHÍNH ── */

export function PublicPortalHome(): ReactNode {
  const [snapshot, setSnapshot] = useState<PublicDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ requestId?: string } | null>(null);
  const pending = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    pending.current?.abort();
    const controller = new AbortController();
    pending.current = controller;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchPublicDashboard(controller.signal);
      if (controller.signal.aborted) return;
      setSnapshot(data);
    } catch (cause) {
      if (controller.signal.aborted) return;
      setError(cause instanceof PublicDashboardError && cause.requestId
        ? { requestId: cause.requestId } : {});
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const refresh = () => { void load(); };
    window.addEventListener("focus", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      pending.current?.abort();
    };
  }, [load]);

  const empty = snapshot && statistics.every(({ key }) => snapshot[key] === 0);


  return (
    <section className="pub-home" aria-label="Cổng thông tin nghĩa trang liệt sĩ">

      <section className="memorial-band" aria-label="Thống kê nghĩa trang">
        <div className="memorial-inner">
          <div className="band-head"><span>TỔNG QUAN NGHĨA TRANG</span><span>HOẠT ĐỘNG TƯỞNG NIỆM</span></div>
          <div className="memorial-stats" aria-busy={loading}>
            {statistics.map(({ key, label, icon }) => {
              const rawValue = (snapshot && DISPLAY_OVERRIDE[key]) ?? snapshot?.[key];
              const value = rawValue ?? "—";
              const isGraveCard = key === "graveCount";
              const identifiedCount = typeof rawValue === "number"
                ? Math.max(0, rawValue - UNIDENTIFIED_GRAVES_DEMO)
                : undefined;
              const unidentifiedCount = typeof rawValue === "number"
                ? Math.min(rawValue, UNIDENTIFIED_GRAVES_DEMO)
                : undefined;
              return (
                <Card
                  key={key}
                  className={`memorial-stat${key === "dailyIncenseCount" ? " incense-stat" : ""}`}
                  role="group"
                  aria-label={label}
                >
                  <svg viewBox="0 0 64 64" aria-hidden="true"><path d={icon} /></svg>
                  {loading && !snapshot ? (
                    <Skeleton.Input active aria-hidden="true" />
                  ) : (
                    <Statistic value={value} groupSeparator="." />
                  )}
                  <h2>{label}</h2>
                  {isGraveCard && snapshot && (
                    <div className="memorial-sub-bar" aria-label="Phân loại danh tính mộ">
                      <div className="memorial-sub-cell">
                        <span className="sub-cell-label">{"Đã xác định danh tính"}</span>
                        <strong className="sub-cell-value">
                          {identifiedCount !== undefined ? identifiedCount.toLocaleString("vi-VN") : "—"}
                        </strong>
                      </div>
                      <div className="memorial-sub-cell">
                        <span className="sub-cell-label">{"Chưa xác định danh tính"}</span>
                        <strong className="sub-cell-value">
                          {unidentifiedCount !== undefined ? unidentifiedCount.toLocaleString("vi-VN") : "—"}
                        </strong>
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
        {error ? (
          <Alert
            className="pub-stat-error"
            type={snapshot ? "warning" : "error"}
            role="alert"
            title={snapshot ? "Số liệu cũ: chưa cập nhật được số liệu mới." : "Không tải được số liệu. Dấu “—” không phải số 0."}
            description={error.requestId ? `Mã hỗ trợ: ${error.requestId}` : undefined}
            action={<Button onClick={() => { void load(); }}>Thử lại</Button>}
          />
        ) : loading || empty ? (
          <p className="pub-stat-status" role="status" aria-live="polite">
            {loading ? "Đang tải số liệu…" : "Chưa có dữ liệu công khai hoặc hoạt động được ghi nhận."}
          </p>
        ) : null}
      </section>
      <div className="pub-home-map"><PublicCemeteryMap /></div>

      {/* ── 3 PHIẾN KÍNH DI SẢN (HERITAGE TECH MONUMENTS) ── */}
      <div className="pub-pillars">
        <div className="pub-section-header">
          <div className="pub-section-badge">
            <IconNationalStar />
            <span>NĂNG LỰC SỐ HÓA KHÔNG GIAN</span>
          </div>
          <h2 className="pub-section-title">Nền tảng Tưởng niệm & Dữ liệu Mở</h2>
        </div>

        <div className="pub-pillars-grid">
          {PILLARS.map((p, idx) => (
            <div key={p.title} className="pub-pillar-card">
              <div className="pub-pillar-card-top">
                <div className="pub-pillar-icon-box">
                  {idx === 0 && <IconSpatial3D />}
                  {idx === 1 && <IconLocationGrid />}
                  {idx === 2 && <IconSacredScroll />}
                </div>
                <span className="pub-pillar-badge">{p.tag}</span>
              </div>
              <h3 className="pub-pillar-title">{p.title}</h3>
              <p className="pub-pillar-desc">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── HÀNH TRÌNH 3 BƯỚC CHIÊM BÁI SỐ (THE SACRED PILGRIMAGE) ── */}
      <div className="pub-guide-section">
        <section className="pub-guide" aria-labelledby="pub-guide-title">
          <div className="pub-guide-head">
            <div className="pub-guide-badge">
              <IconNationalStar />
              <span>HÀNH TRÌNH TRI ÂN SỐ</span>
            </div>
            <Typography.Title level={2} id="pub-guide-title" className="pub-guide-title">
              {GUIDE_TITLE}
            </Typography.Title>
            <p className="pub-guide-sub">
              Chỉ với vài thao tác đơn giản để tìm thấy và chiêm bái phần mộ người thân trên không gian số
            </p>
          </div>

          <ol className="pub-steps">
            {GUIDE_STEPS.map((step, idx) => (
              <li key={step.title} className="pub-step">
                <div className="pub-step-header">
                  <div className="pub-step-num" aria-hidden="true">
                    0{idx + 1}
                  </div>
                  <span className="pub-step-tag">CHẶNG 0{idx + 1}</span>
                </div>
                <div className="pub-step-body">
                  <h3 className="pub-step-title">{step.title}</h3>
                  <p className="pub-step-desc">{step.desc}</p>
                  <div className="pub-step-hint-box">
                    <p className="pub-step-hint">{step.hint}</p>
                  </div>
                </div>
              </li>
            ))}
          </ol>

          <div className="pub-guide-cta">
            <a className="pub-cta-primary pub-cta-primary--unified" href="#pub-map-title">
              <span className="pub-cta-icon">
                <IconSpatialCompass />
              </span>
              <span>{SCENE_CTA}</span>
              <span className="pub-cta-arrow" aria-hidden="true">→</span>
            </a>
          </div>
        </section>
      </div>

      {/* Cảnh báo mô phỏng hỗ trợ trợ năng (sr-only, Spec §6.2) */}
      <span className="pub-sr-only">{SIMULATION_NOTICE}</span>

    </section>
  );
}
